import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./StartSit.css";
import "./TableStyles.css";
import { getDvpColor } from "./dvpColor";
import {
  weekStatsUrl,
  seasonStatsUrl,
  scoringStatKeys,
  reduceStatRows,
  fptsPerGame,
  recentForm,
  playedWeeksBefore,
} from "./playerForm";
import { useSleeperAuth } from "./auth";
import { projectedPoints, hasProjection, fetchWeekProjectionsUrl } from "./leagueScoring";
import { evaluateLineup, countActionable, benchFrom } from "./startSitModel";
import { fetchMatchups, startersFor } from "./sleeperLineup";
import { lockedTeamsFor, byeTeamsFor } from "./nflSchedule";
import PositionPill from "./PositionPill";
import { positionKey } from "./positionColors";
import { loadHiddenLeagues, visibleLeagues, useHiddenLeagues } from "./leagueVisibility";

const SEASON = 2026;
const BASE_URL = process.env.REACT_APP_MOCK
  ? "http://localhost:5000"
  : "https://shaggy-latashia-carnade-2ea2054a.koyeb.app";

// Sleeper and nflverse spell a few teams differently.
const TEAM_ABBR_MAP = { LAR: "LA", WSH: "WAS", JAC: "JAX" };
const nflverseTeam = (team) => TEAM_ABBR_MAP[team] || team;

// nflverse only ranks defences against these positions.
const DVP_POSITIONS = new Set(["qb", "rb", "wr", "te"]);

// Remembered so looking at someone else's leagues survives navigating away.
const OVERRIDE_KEY = "startsit_use_menu_name";

const PROJECTION_TTL_MS = 30 * 60 * 1000;
const DFS_TTL_MS = 60 * 60 * 1000;
// Finished weeks do not change, so these can be held longer than projections.
const STATS_TTL_MS = 6 * 60 * 60 * 1000;

function readCache(key, ttl) {
  try {
    const raw = sessionStorage.getItem(key);
    const at = sessionStorage.getItem(`${key}_timestamp`);
    if (!raw || !at || Date.now() - parseInt(at, 10) > ttl) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeCache(key, value) {
  // Storage can be full or blocked; the page works without it.
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
    sessionStorage.setItem(`${key}_timestamp`, Date.now().toString());
  } catch {
    /* ignore */
  }
}

// Sleeper publishes ~3300 rows a week but only a few hundred carry a real
// projection. Keep just what a lineup needs, so this fits in sessionStorage —
// the raw payload is over 2 MB and would blow the quota.
function reduceProjections(rows) {
  const out = {};
  for (const row of Array.isArray(rows) ? rows : []) {
    const player = row.player || {};
    out[String(row.player_id)] = {
      stats: row.stats || null,
      team: row.team || player.team_abbr || player.team || null,
      opponent: row.opponent || null,
      name: [player.first_name, player.last_name].filter(Boolean).join(" ").trim(),
      position: player.position || null,
      fantasyPositions: player.fantasy_positions || null,
      injuryStatus: player.injury_status || null,
    };
  }
  return out;
}

// DFS rows are keyed "{id}_W{week}_D{date}"; index them by player, keeping the
// earliest game so a player is judged against the game they actually play next.
function indexDfs(data) {
  const out = {};
  for (const row of Object.values(data || {})) {
    const id = row?.sleeper_id;
    if (!id) continue;
    const existing = out[id];
    if (!existing || String(row.game_date || "") < String(existing.game_date || "")) {
      out[id] = row;
    }
  }
  return out;
}

// Sleeper's own lineup change, captured from its web app. `starters` is the whole
// slot-ordered array, so a swap is that array with one index replaced. leg and
// round are both the NFL week in the regular season.
async function submitLineup({ leagueId, rosterId, week, starters, token, signal }) {
  const list = starters.map((id) => JSON.stringify(String(id))).join(",");
  const query = `mutation update_matchup_leg($starters_games: Map) {
    update_matchup_leg(league_id: ${JSON.stringify(String(leagueId))},roster_id: ${Number(rosterId)},leg: ${Number(week)},round: ${Number(week)},starters: [${list}],starters_games: $starters_games){
      league_id leg matchup_id roster_id round starters players starters_games
    }
  }`;

  const res = await fetch("https://sleeper.com/graphql", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-platform": "web",
      authorization: token,
      "x-sleeper-graphql-op": "update_matchup_leg",
    },
    body: JSON.stringify({ operationName: "update_matchup_leg", variables: {}, query }),
    signal,
  });

  const json = await res.json().catch(() => null);
  // Sleeper answers 200 with an errors array rather than an HTTP error code.
  if (json?.errors?.length) throw new Error(json.errors[0]?.message || "Sleeper rejected the change");
  if (!res.ok) throw new Error(`Sleeper returned ${res.status}`);
  const updated = json?.data?.update_matchup_leg;
  if (!updated?.starters) throw new Error("Sleeper returned no lineup");
  return updated;
}

function fmt(value, decimals = 1) {
  if (value == null || value === "" || Number.isNaN(Number(value))) return "—";
  return Number(value).toFixed(decimals);
}

function TeamCell({ team, opponent }) {
  if (!team) return <span className="ss-muted">—</span>;
  return (
    <span className="ss-matchup">
      {team}
      {opponent ? <span className="ss-vs"> vs {opponent}</span> : <span className="ss-vs ss-bye"> bye</span>}
    </span>
  );
}

function DvpCell({ rank }) {
  if (!rank) return <span className="ss-muted">—</span>;
  const color = getDvpColor(rank);
  return (
    <span style={color ? { color, fontWeight: 600 } : undefined} title="Opponent rank vs this position (1 = toughest)">
      {rank}
    </span>
  );
}

function StatusCell({ player }) {
  const status = player?.status;
  if (!status) return <span className="ss-muted">—</span>;
  return <span className={`ss-status ss-status-${String(status).replace(/\s+/g, "")}`}>{status}</span>;
}

const VERDICT_LABEL = {
  sit: "Sit",
  start: "Start",
  tossup: "Toss-up",
  locked: "Locked",
};

function VerdictCell({ row }) {
  const label = VERDICT_LABEL[row.verdict] || "—";
  const reason =
    row.verdict === "sit" && row.reason === "out" ? "not playing"
      : row.verdict === "sit" && row.reason === "bye" ? "on bye"
      : row.verdict === "sit" && row.reason === "empty" ? "slot empty"
      : row.verdict === "sit" && row.reason === "no projection" ? "no projection"
      : null;
  return (
    <span className={`ss-verdict ss-verdict-${row.verdict}`} title={reason || undefined}>
      {row.verdict === "locked" ? "🔒 " : ""}{label}
    </span>
  );
}

function SuggestionCell({ row, canEdit, onSwap, busy }) {
  if (row.verdict === "locked") return <span className="ss-muted">—</span>;
  if (row.noReplacement) return <span className="ss-muted">no eligible replacement</span>;
  if (row.benchSpokenFor) {
    return (
      <span className="ss-muted" title="The bench players who fit this slot are needed by another one">
        bench used elsewhere
      </span>
    );
  }
  if (!row.suggestion) return <span className="ss-muted">—</span>;
  const delta = row.delta;
  // Neither side may have kicked off: the outgoing player is leaving a lineup
  // that has already counted, and the incoming one cannot be added late.
  const swappable = canEdit && !row.locked && !row.starter?.locked && !row.suggestion.locked;
  return (
    <span className="ss-suggestion">
      → {row.suggestion.name}
      {delta != null && <span className="ss-delta"> (+{delta.toFixed(1)})</span>}
      {swappable && (
        <button type="button" className="ss-swap" onClick={onSwap} disabled={busy}>
          {busy ? "…" : "Make the change"}
        </button>
      )}
    </span>
  );
}

// Whether the position beside a player's name is telling you anything.
//
// In a QB slot the starter is a quarterback, so the slot pill and the position
// pill are two identical chips sitting next to each other. Only a flex slot —
// or the odd case where someone is in a slot their position does not match —
// makes the second one worth drawing.
function showPositionBeside(slot, position) {
  if (!position) return false;
  return positionKey(slot) !== positionKey(position);
}

function PlayerRow({ row, canEdit, onSwap, busy, formWindow }) {
  const p = row.starter;
  // Every unlocked slot with somewhere to go can be changed, not only the flagged
  // ones — otherwise a toss-up could never be acted on.
  const changeable = canEdit && !row.locked && !p?.locked && (row.candidates?.length || 0) > 0;
  const label = p ? p.name : "empty";
  return (
    <tr className={`ss-row ss-row-${row.verdict}`}>
      <td className="ss-slot"><PositionPill position={row.slot} /></td>
      <td className="ss-player">
        {changeable ? (
          <button type="button" className="ss-player-btn" onClick={onSwap} title="Change this slot">
            {label}
          </button>
        ) : (
          <span className={p ? undefined : "ss-muted"}>{label}</span>
        )}
        {showPositionBeside(row.slot, p?.position) && (
          <span className="ss-pos-pill">
            <PositionPill position={p.position} />
          </span>
        )}
      </td>
      <td><TeamCell team={p?.team} opponent={p?.opponent} /></td>
      <td className="ss-num" title={p && !p.hasProjection ? "Sleeper has no projection for this player" : undefined}>
        {p?.hasProjection ? fmt(p.proj) : <span className="ss-muted">—</span>}
      </td>
      <td className="ss-num">{fmt(p?.fptsG)}</td>
      <td className="ss-num ss-num-form"><FormCell average={p?.l5} games={p?.l5Games} window={formWindow} /></td>
      <td className="ss-num"><DvpCell rank={p?.dvpRank} /></td>
      <td><StatusCell player={p} /></td>
      <td><VerdictCell row={row} /></td>
      <td className="ss-suggestion-cell">
        <SuggestionCell row={row} canEdit={canEdit} onSwap={onSwap} busy={busy} />
      </td>
    </tr>
  );
}

// The count matters: a two-game average is not a five-game one, and early in a
// season L5 is necessarily short.
// The heading says how many weeks were available; a row only speaks up when it
// found fewer, which is a quarter of rostered players and exactly the ones whose
// average deserves the least trust — a "1" is a single game, not an average.
function FormCell({ average, games, window }) {
  if (average == null) return <span className="ss-muted">—</span>;
  const short = games > 0 && games < (window || 5);
  return (
    <>
      {fmt(average)}
      {short && (
        <span className="ss-form-games" title={`Only ${games} of the last ${window} games played`}>
          ({games})
        </span>
      )}
    </>
  );
}

// A bench player's own position, and only that.
//
// This cell used to list the slots he fits, which in a SuperFlex league meant
// every bench player carried an SFLX beside his position — true of all of them,
// so it distinguished nobody and read as part of the furniture. What a bench is
// scanned for is who is on it, and the position answers that.
function BenchRow({ player, formWindow }) {
  return (
    <tr className="ss-row ss-row-bench">
      <td className="ss-slot">
        {player.position ? (
          <PositionPill position={player.position} />
        ) : (
          <span className="ss-muted">—</span>
        )}
      </td>
      <td className="ss-player">{player.name}</td>
      <td><TeamCell team={player.team} opponent={player.opponent} /></td>
      <td className="ss-num">{player.hasProjection ? fmt(player.proj) : <span className="ss-muted">—</span>}</td>
      <td className="ss-num">{fmt(player.fptsG)}</td>
      <td className="ss-num ss-num-form"><FormCell average={player.l5} games={player.l5Games} window={formWindow} /></td>
      <td className="ss-num"><DvpCell rank={player.dvpRank} /></td>
      <td><StatusCell player={player} /></td>
      <td className="ss-muted">bench</td>
      <td />
    </tr>
  );
}

// Columns in the lineup table, for the rows that span the whole of it.
const COLUMNS = 10;

function LeagueCard({ league, onlyActionable, benchOpen, onToggleBench, canEdit, onSwap, busyRow, formWindow }) {
  const { rows, bench, flagged } = league;
  const shown = onlyActionable ? rows.filter((r) => r.verdict === "sit") : rows;

  return (
    <section className="ss-card">
      <header className="ss-card-head">
        <h3 className="ss-league-name">{league.name}</h3>
        <span className={`ss-flag-count ${flagged ? "ss-flag-on" : ""}`}>
          {flagged ? `${flagged} to look at` : "lineup looks right"}
        </span>
      </header>
      <div className="ss-table-wrap">
        <table className="ss-table">
          <thead>
            <tr>
              <th>Slot</th><th>Player</th><th>Matchup</th>
              <th className="ss-num" title="Sleeper's projection for this week, priced in this league's scoring — so TE premium and SuperFlex are included">
                Proj
              </th>
              <th className="ss-num" title="Points per game across the season so far, in this league's scoring">
                FPTS/G
              </th>
              <th
                className="ss-num ss-num-form"
                title={
                  `Average over the last ${formWindow || 5} game${formWindow === 1 ? "" : "s"} played, ` +
                  "in this league's scoring. A player who missed a week shows his own count beside the figure."
                }
              >
                L5{formWindow > 0 && formWindow < 5 && <span className="ss-form-games">({formWindow})</span>}
              </th>
              <th className="ss-num" title="Opponent's rank against this position, 1 = toughest">DvP</th>
              <th>Status</th><th>Verdict</th><th>Suggestion</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((row) => (
              <PlayerRow
                formWindow={formWindow}
                key={`${row.slot}-${row.index}`}
                row={row}
                canEdit={canEdit}
                busy={busyRow === `${league.id}:${row.index}`}
                onSwap={() => onSwap(league, row)}
              />
            ))}
            {/*
              The toggle is a row of its own, between the starters and the bench,
              rather than a button under the table. Under the table it was pushed
              down by whatever it had just revealed, so closing a long bench meant
              scrolling to find the control that had been under the cursor a
              moment earlier. Here it stays put and the bench opens beneath it.
            */}
            {bench.length > 0 && (
              <tr className="ss-bench-row">
                <td colSpan={COLUMNS}>
                  <button className="ss-bench-toggle" onClick={onToggleBench} aria-expanded={benchOpen}>
                    {benchOpen ? "▼" : "►"} Bench ({bench.length})
                  </button>
                </td>
              </tr>
            )}
            {benchOpen && bench.map((p) => <BenchRow key={p.id} player={p} formWindow={formWindow} />)}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function StartSit({ userName }) {
  const auth = useSleeperAuth();
  const [week, setWeek] = useState(null);
  // How many weeks the L5 average has to draw on. Early in a season that is
  // fewer than five, and it belongs in the heading rather than repeated down
  // every row — most players will have found exactly this many.
  const formWindow = week ? playedWeeksBefore(week, 5).length : 0;
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [onlyActionable, setOnlyActionable] = useState(false);
  const [openBenches, setOpenBenches] = useState(() => new Set());
  const [busyRow, setBusyRow] = useState(null);
  const [pendingSwap, setPendingSwap] = useState(null);
  const [swapError, setSwapError] = useState(null);
  const [problems, setProblems] = useState([]);
  const projectionsRef = useRef(null);

  const [useMenuName, setUseMenuName] = useState(() => {
    try {
      return localStorage.getItem(OVERRIDE_KEY) === "1";
    } catch {
      return false;
    }
  });

  // Signing in normally decides whose lineups these are, but the menu name can
  // take over — the page reads only public data, so any user works.
  const loginName = auth?.display_name || null;
  const menuName = (userName || "").trim();
  const canOverride =
    !!loginName && !!menuName && menuName.toLowerCase() !== loginName.toLowerCase();
  const displayName = canOverride && useMenuName ? menuName : loginName || menuName;
  const hiddenLeagues = useHiddenLeagues();

  // Writing a lineup acts as whoever the token belongs to, so it is offered only
  // when the page is showing that same account. With the override on, the lineup
  // on screen belongs to someone else and must stay read-only.
  const canEdit =
    !!auth?.token && !!loginName && displayName.toLowerCase() === loginName.toLowerCase();

  const toggleOverride = (checked) => {
    setUseMenuName(checked);
    try {
      localStorage.setItem(OVERRIDE_KEY, checked ? "1" : "0");
    } catch {
      /* preference is not important enough to fail over */
    }
  };

  const load = useCallback(async (name, signal) => {
    setLoading(true);
    setError(null);
    try {
      const state = await fetch("https://api.sleeper.app/v1/state/nfl", { signal }).then((r) => r.json());
      const currentWeek = state?.week > 0 ? state.week : 1;
      setWeek(currentWeek);

      const user = await fetch(`https://api.sleeper.app/v1/user/${name}`, { signal }).then((r) => r.json());
      if (!user?.user_id) throw new Error(`No Sleeper user called "${name}"`);

      const allLeagues = await fetch(
        `https://api.sleeper.app/v1/user/${user.user_id}/leagues/nfl/${SEASON}`,
        { signal }
      ).then((r) => r.json());

      // Head-to-head only: best ball has no lineup to set. Leagues switched off
      // in Settings are dropped here too, so they never reach a request.
      const playable = visibleLeagues(
        (Array.isArray(allLeagues) ? allLeagues : []).filter((l) => {
          const t = l.settings?.type;
          return (t === 0 || t === 2) && l.settings?.best_ball !== 1;
        }),
        loadHiddenLeagues()
      );
      if (playable.length === 0) {
        setLeagues([]);
        return;
      }

      const projKey = `sleeper_proj_week_${currentWeek}`;
      const dfsKey = `dfs_projections_week_${currentWeek}`;

      // Everything below that comes from our own backend is optional: the page is
      // still useful without it, but a silently blank column is indistinguishable
      // from a player having no data, so failures are collected and shown.
      const failed = [];

      // Only the stats these leagues actually score are kept, which takes a week
      // from ~640 KB to ~40 KB. The set is stored with the payload so a cache
      // trimmed for a narrower set of leagues is refetched rather than reused.
      const keepKeys = scoringStatKeys(playable);
      const statsCache = (key, url) => async () => {
        const cached = readCache(key, STATS_TTL_MS);
        if (cached?.stats && [...keepKeys].every((k) => cached.keys?.includes(k))) {
          return cached.stats;
        }
        try {
          const rows = await fetch(url, { signal }).then((r) => {
            if (!r.ok) throw new Error(String(r.status));
            return r.json();
          });
          const stats = reduceStatRows(rows, keepKeys);
          writeCache(key, { keys: [...keepKeys], stats });
          return stats;
        } catch (err) {
          if (err.name !== "AbortError") failed.push("recent form");
          return {};
        }
      };

      const formWeeks = playedWeeksBefore(currentWeek, 5);

      const [projections, dfsRaw, teamStats, schedule, rosterLists, matchupLists, seasonStats, weeklyStats] = await Promise.all([
        (async () => {
          if (projectionsRef.current?.week === currentWeek) return projectionsRef.current.data;
          const cached = readCache(projKey, PROJECTION_TTL_MS);
          if (cached) return cached;
          const rows = await fetch(fetchWeekProjectionsUrl(SEASON, currentWeek), { signal }).then((r) => r.json());
          const reduced = reduceProjections(rows);
          writeCache(projKey, reduced);
          return reduced;
        })(),
        (async () => {
          const cached = readCache(dfsKey, DFS_TTL_MS);
          if (cached) return cached;
          // Only the current week is kept server-side; an older week 404s and the
          // page simply goes without recent-form numbers.
          try {
            const res = await fetch(`${BASE_URL}/dfs-salaries/week/${currentWeek}`, { signal });
            if (!res.ok) throw new Error(String(res.status));
            const data = await res.json();
            writeCache(dfsKey, data);
            return data;
          } catch (err) {
            // Recent form no longer comes from here — this is now only a
            // fallback for a player's name, team and opponent.
            if (err.name !== "AbortError") failed.push("player details");
            return {};
          }
        })(),
        fetch(`${BASE_URL}/stats/teams`, { signal })
          .then((r) => {
            if (!r.ok) throw new Error(String(r.status));
            return r.json();
          })
          // Older backends wrap this in {teams: [...]}; accept either shape.
          .then((d) => (Array.isArray(d) ? d : d?.teams || []))
          .catch((err) => {
            if (err.name !== "AbortError") failed.push("matchup ranks");
            return [];
          }),
        fetch(`https://api.sleeper.app/schedule/nfl/regular/${SEASON}`, { signal })
          .then((r) => (r.ok ? r.json() : [])).catch(() => []),
        Promise.all(
          playable.map((l) =>
            fetch(`https://api.sleeper.app/v1/league/${l.league_id}/rosters`, { signal })
              .then((r) => r.json())
              .catch(() => [])
          )
        ),
        // The lineup itself comes from here rather than from the roster above.
        // See sleeperLineup.js: the roster's own `starters` lags, and this is
        // also where a change is written, so reading it keeps the two in step.
        Promise.all(
          playable.map((l) => fetchMatchups(l.league_id, currentWeek, { signal }))
        ),
        statsCache(`sleeper_stats_season_${SEASON}`, seasonStatsUrl(SEASON))(),
        // Newest week first, which is the order recentForm walks.
        Promise.all(
          formWeeks.map((w) =>
            statsCache(`sleeper_stats_week_${SEASON}_${w}`, weekStatsUrl(SEASON, w))()
          )
        ),
      ]);

      projectionsRef.current = { week: currentWeek, data: projections };

      const dfsById = indexDfs(dfsRaw);
      const locked = lockedTeamsFor(schedule, currentWeek);
      const byeTeams = byeTeamsFor(schedule, currentWeek);
      const teamByAbbr = {};
      for (const t of Array.isArray(teamStats) ? teamStats : []) {
        if (t?.team) teamByAbbr[t.team] = t;
      }

      // One metadata call for every rostered player across every league. It is the
      // freshest injury source we have — the backend refreshes it hourly on gamedays.
      const rosters = playable.map((l, i) => {
        const list = Array.isArray(rosterLists[i]) ? rosterLists[i] : [];
        return list.find((r) => r.owner_id === user.user_id) || null;
      });
      const everyId = new Set();
      rosters.forEach((r) => (r?.players || []).forEach((id) => everyId.add(id)));

      let meta = {};
      if (everyId.size > 0) {
        meta = await fetch(`${BASE_URL}/getplayers/data`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ playerlist: [...everyId] }),
          signal,
        })
          .then((r) => {
            if (!r.ok) throw new Error(String(r.status));
            return r.json();
          })
          .then((d) => d.players || d || {})
          .catch((err) => {
            if (err.name !== "AbortError") failed.push("injury status");
            return {};
          });
      }

      const built = playable.map((league, i) => {
        const roster = rosters[i];
        const starters = startersFor(matchupLists[i], roster);
        const reserve = roster?.reserve || [];
        const taxi = roster?.taxi || [];
        const all = roster?.players || [];
        const bench = benchFrom({ players: all, starters, reserve, taxi });

        const playerById = {};
        for (const id of new Set([...all, ...starters])) {
          if (!id || id === "0") continue;
          const proj = projections[String(id)] || {};
          const info = meta[id] || {};
          const dfs = dfsById[id] || {};
          const position = proj.position || info.position || dfs.position || null;
          const team = proj.team || info.team || dfs.team || null;
          const opponent = proj.opponent || dfs.opponent || null;
          const status = info.injury_status || proj.injuryStatus || null;
          const posKey = String(position || "").toLowerCase();
          const oppStats = teamByAbbr[nflverseTeam(opponent)];

          playerById[id] = {
            id,
            name:
              proj.name ||
              [info.first_name, info.last_name].filter(Boolean).join(" ").trim() ||
              dfs.name ||
              id,
            position,
            fantasyPositions: proj.fantasyPositions || (position ? [position] : []),
            team,
            opponent,
            proj: projectedPoints(proj.stats, league.scoring_settings),
            hasProjection: hasProjection(proj.stats),
            status,
            // From the schedule, not from injury_status, which only says "Bye"
            // when the player has no other designation.
            onBye: status === "Bye" || (!!team && byeTeams.has(team)),
            locked: !!team && locked.has(team),
            // Priced in this league's scoring, so these sit on the same scale
            // as Proj. The DFS scrape's own l5_avg is DraftKings scoring and
            // cannot be compared with it — a TE premium alone moved Trey
            // McBride 4.3 points a game.
            fptsG: fptsPerGame(seasonStats[id], league.scoring_settings, projectedPoints),
            ...(() => {
              const form = recentForm(weeklyStats, id, league.scoring_settings, projectedPoints);
              return { l5: form.average, l5Games: form.games };
            })(),
            dvpRank: DVP_POSITIONS.has(posKey)
              ? oppStats?.def_rank_vs_position?.season?.[posKey] ?? null
              : null,
          };
        }

        const rows = evaluateLineup({
          rosterPositions: league.roster_positions || [],
          starters,
          bench,
          playerById,
        });

        return {
          id: league.league_id,
          name: league.name,
          // Same test the league page uses: type 2 is dynasty, 0 is redraft.
          isDynasty: league.settings?.type === 2,
          // Kept so a lineup change can be submitted and applied without refetching.
          rosterId: roster?.roster_id ?? null,
          // Sleeper's lineup mutation returns neither of these, and a swap cannot
          // change them, so they are carried over rather than re-fetched.
          reserveIds: reserve,
          taxiIds: taxi,
          rosterPositions: league.roster_positions || [],
          starterIds: starters,
          benchIds: bench,
          playerById,
          rows,
          bench: bench.map((id) => playerById[id]).filter(Boolean).sort((a, b) => (b.proj ?? 0) - (a.proj ?? 0)),
          flagged: countActionable(rows),
        };
      });

      built.sort((a, b) => Number(b.isDynasty) - Number(a.isDynasty));
      setLeagues(built);
      setProblems(failed);
    } catch (err) {
      if (err.name !== "AbortError") setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!displayName) return undefined;
    const controller = new AbortController();
    load(displayName, controller.signal);
    return () => controller.abort();
    // hiddenLeagues is a dependency so hiding a league in Settings takes effect
    // on return without a reload; load() reads the current set itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayName, load, hiddenLeagues]);

  const totalFlagged = useMemo(
    () => leagues.reduce((n, l) => n + l.flagged, 0),
    [leagues]
  );

  const visible = useMemo(
    () => leagues.filter((l) => !onlyActionable || l.flagged > 0),
    [leagues, onlyActionable]
  );

  // Clicking only opens the confirmation; nothing is sent until it is accepted.
  const requestSwap = (league, row) => {
    if (!canEdit) return;
    const options = row.candidates || [];
    if (row.locked || row.starter?.locked || options.length === 0) return;
    setSwapError(
      league.rosterId == null ? "Could not tell which roster is yours in this league." : null
    );
    // The recommendation is preselected, so the common case is still open-and-confirm.
    setPendingSwap({ league, row, selectedId: (row.suggestion || options[0]).id });
  };

  const closeSwap = () => {
    if (busyRow) return;
    setPendingSwap(null);
    setSwapError(null);
  };

  const confirmSwap = async () => {
    if (!pendingSwap) return;
    const { league, row, selectedId } = pendingSwap;
    const outgoing = row.starter;
    const incoming =
      (row.candidates || []).find((c) => c.id === selectedId) || row.suggestion;
    if (!canEdit || !incoming || league.rosterId == null) return;
    // The same checks the button applies, repeated because this is what writes.
    if (row.locked || outgoing?.locked || incoming.locked) {
      setSwapError("That game has already started, so the lineup is locked.");
      return;
    }

    const rowKey = `${league.id}:${row.index}`;
    setBusyRow(rowKey);
    try {
      const nextStarters = [...league.starterIds];
      nextStarters[row.index] = incoming.id;

      const updated = await submitLineup({
        leagueId: league.id,
        rosterId: league.rosterId,
        week,
        starters: nextStarters,
        token: auth.token,
      });

      // Rebuild from what Sleeper says the lineup now is, not from what was sent.
      const confirmedStarters = updated.starters.map(String);
      // updated.players is the whole roster and the response carries no taxi or
      // reserve list, so those have to come from what was loaded. Without them
      // every taxi and IR player rejoined the bench and became suggestable.
      const benchIds = benchFrom({
        players: updated.players || league.benchIds,
        starters: confirmedStarters,
        reserve: league.reserveIds,
        taxi: league.taxiIds,
      });

      setLeagues((prev) =>
        prev.map((l) => {
          if (l.id !== league.id) return l;
          const rows = evaluateLineup({
            rosterPositions: l.rosterPositions,
            starters: confirmedStarters,
            bench: benchIds,
            playerById: l.playerById,
          });
          return {
            ...l,
            starterIds: confirmedStarters,
            benchIds,
            rows,
            bench: benchIds
              .map((id) => l.playerById[id])
              .filter(Boolean)
              .sort((a, b) => (b.proj ?? 0) - (a.proj ?? 0)),
            flagged: countActionable(rows),
          };
        })
      );
      setPendingSwap(null);
      setSwapError(null);
    } catch (err) {
      // Kept in the dialog rather than thrown away in a second popup, so the
      // change it refers to is still on screen.
      setSwapError(
        /illegal/i.test(err.message)
          ? "Sleeper would not allow it — a game may have started since this page loaded, "
            + "or the player is not eligible for that slot."
          : err.message
      );
    } finally {
      setBusyRow(null);
    }
  };

  const allBenchesOpen =
    visible.length > 0 && visible.every((l) => openBenches.has(l.id));

  const toggleAllBenches = () =>
    setOpenBenches(allBenchesOpen ? new Set() : new Set(visible.map((l) => l.id)));

  if (!displayName) {
    return (
      <div className="ss-page">
        <h1 className="ss-title">Start/Sit</h1>
        <p className="ss-empty">
          To see your lineups, enter a user name in the menu or sign in with Sleeper in
          Settings — either one is enough.
        </p>
      </div>
    );
  }

  return (
    <div className="ss-page">
      <header className="ss-head">
        <h1 className="ss-title">
          Start/Sit{week ? <span className="ss-week"> — week {week}</span> : null}
        </h1>
        {canOverride && (
          <div className="ss-whose">
            <label className="ss-override">
              <input
                type="checkbox"
                checked={useMenuName}
                onChange={(e) => toggleOverride(e.target.checked)}
              />
              Use the menu name ({menuName}) instead of {loginName}
            </label>
            {useMenuName && <span className="ss-whose-note">showing {displayName}'s leagues</span>}
          </div>
        )}
        {!loading && leagues.length > 0 && (
          <div className="ss-summary">
            <span>{leagues.length} leagues</span>
            <span className={totalFlagged ? "ss-flag-on" : ""}>
              {totalFlagged} to look at
            </span>
            <label className="ss-filter">
              <input
                type="checkbox"
                checked={onlyActionable}
                onChange={(e) => setOnlyActionable(e.target.checked)}
              />
              Only show flagged
            </label>
            <button
              type="button"
              className="ss-bulk-btn"
              onClick={toggleAllBenches}
              aria-expanded={allBenchesOpen}
            >
              <span>{allBenchesOpen ? "Collapse benches" : "Expand benches"}</span>
              {/* Sized for the longer label so the button never changes width. */}
              <span className="ss-bulk-ghost" aria-hidden="true">Collapse benches</span>
            </button>
          </div>
        )}
      </header>

      {!loading && problems.length > 0 && (
        <p className="ss-warning" title={BASE_URL}>
          Couldn't reach the stats backend for {problems.join(" and ")}, so those columns
          show “—”. Everything else comes straight from Sleeper and is unaffected.
        </p>
      )}

      {loading && <p className="ss-empty">Reading your lineups…</p>}
      {error && <p className="ss-error">{error}</p>}
      {!loading && !error && leagues.length === 0 && (
        <p className="ss-empty">No head-to-head leagues found for {displayName}.</p>
      )}

      {!loading && visible.map((league, i) => {
        const previous = visible[i - 1];
        const startsGroup = !previous || previous.isDynasty !== league.isDynasty;
        return (
          <React.Fragment key={league.id}>
            {startsGroup && (
              <h2 className="ss-group-header">{league.isDynasty ? "Dynasty" : "Redraft"}</h2>
            )}
            <LeagueCard
              league={league}
              formWindow={formWindow}
              onlyActionable={onlyActionable}
              canEdit={canEdit}
              onSwap={requestSwap}
              busyRow={busyRow}
              benchOpen={openBenches.has(league.id)}
              onToggleBench={() =>
                setOpenBenches((prev) => {
                  const next = new Set(prev);
                  if (next.has(league.id)) next.delete(league.id);
                  else next.add(league.id);
                  return next;
                })
              }
            />
          </React.Fragment>
        );
      })}

      {!loading && leagues.length > 0 && (
        <p className="ss-footnote">
          Projections are Sleeper's, priced with each league's own scoring. A suggestion means
          the bench player projects at least a point higher — small gaps sit inside the
          projection's own error, so treat them as a prompt to look, not an instruction.
          One player is only ever suggested for one slot, and only where the swap is
          actually recommended. Where a starter is on bye or ruled out, anyone who can play
          is an improvement, so a backup Sleeper does not project may be offered — shown
          with a dash instead of a number.
        </p>
      )}
      {pendingSwap && (
        <div className="ss-modal-overlay" onClick={closeSwap}>
          <div className="ss-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Change this lineup?</h2>
            <p className="ss-modal-league">{pendingSwap.league.name}</p>
            <div className="ss-modal-out">
              <span className="ss-modal-label">Out of {pendingSwap.row.slot}</span>
              <span className="ss-modal-name">
                {pendingSwap.row.starter ? pendingSwap.row.starter.name : "empty slot"}
              </span>
              <span className="ss-modal-proj">
                {pendingSwap.row.starter?.hasProjection ? fmt(pendingSwap.row.starter.proj) : "—"}
              </span>
            </div>

            <span className="ss-modal-label ss-modal-inlabel">Bring in</span>
            <div className="ss-modal-options">
              {(pendingSwap.row.candidates || []).slice(0, 5).map((c) => {
                const outProj = pendingSwap.row.starter?.hasProjection
                  ? pendingSwap.row.starter.proj
                  : null;
                // Sleeper does not project backups, and projectedPoints returns 0
                // for a missing stat line — which must not read as a genuine zero,
                // nor produce a gain figure computed against one.
                const gain =
                  outProj == null || !c.hasProjection ? null : c.proj - outProj;
                const chosen = c.id === pendingSwap.selectedId;
                return (
                  <button
                    key={c.id}
                    type="button"
                    className={`ss-modal-option${chosen ? " ss-modal-option-on" : ""}`}
                    onClick={() => setPendingSwap((prev) => ({ ...prev, selectedId: c.id }))}
                    disabled={!!busyRow}
                  >
                    <span className="ss-modal-opt-name">
                      {c.name}
                      <span className="ss-pos"> {c.position}</span>
                    </span>
                    <span className="ss-modal-opt-proj">
                      {c.hasProjection ? fmt(c.proj) : "—"}
                    </span>
                    <span
                      className={
                        gain == null || gain === 0
                          ? "ss-modal-opt-gain"
                          : gain > 0
                          ? "ss-delta"
                          : "ss-modal-opt-loss"
                      }
                    >
                      {gain == null ? "" : `${gain >= 0 ? "+" : ""}${gain.toFixed(1)}`}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="ss-modal-note">This changes your lineup on Sleeper.</p>
            {swapError && <p className="ss-modal-error">{swapError}</p>}
            <div className="ss-modal-buttons">
              <button
                type="button"
                className="ss-modal-confirm"
                onClick={confirmSwap}
                disabled={!!busyRow}
              >
                {busyRow ? "Changing…" : "Make the change"}
              </button>
              <button type="button" className="ss-modal-cancel" onClick={closeSwap} disabled={!!busyRow}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StartSit;
